import {
  businessDay,
  fmt,
  fmtDate,
  hasValue,
  parseKip,
  parseQty,
  purchaseTotal,
  receivableUnits,
  type Product,
  type PurchaseLine,
} from '@k2pos/core';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddValueSheet } from '@/components/ui/AddValueSheet';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { UnitChip } from '@/components/ui/Chip';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Segment } from '@/components/ui/Segment';
import { Field } from '@/components/ui/Field';
import { Mono, Text } from '@/components/ui/Text';
import { IconClose, IconPlus, IconSearch } from '@/components/icons';
import { cx } from '@/lib/cx';
import { useBackHandling, useGoBack } from '@/hooks/useNav';
import { useMoneyAction } from '@/hooks/useMoneyAction';
import { usePos, type NewPurchase } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Purchase.module.css';

interface Draft extends PurchaseLine {
  key: string;
}

/**
 * ຮັບເຄື່ອງເຂົ້າຮ້ານ — goods in, recorded as a purchase.
 *
 * Note the route is /purchase, not /receive: /receive/:id is already *debt
 * collection*, and reusing the word would collide in paths, filenames and
 * conversation.
 */
export function Purchase() {
  const navigate = useNavigate();
  const goBack = useGoBack('/more');
  const catalog = usePos((s) => s.catalog);
  const suppliers = usePos((s) => s.suppliers);
  const addSupplier = usePos((s) => s.addSupplier);
  const recordPurchase = usePos((s) => s.recordPurchase);

  const [supplier, setSupplier] = useState('');
  // Goods routinely arrive before the paperwork, so the date is editable.
  const [day, setDay] = useState(() => businessDay(new Date()));
  const [lines, setLines] = useState<Draft[]>([]);
  const [paid, setPaid] = useState(true);
  const [cash, setCash] = useState(true);
  const [picking, setPicking] = useState(false);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [query, setQuery] = useState('');

  const total = useMemo(() => purchaseTotal(lines), [lines]);
  const valid = supplier.trim().length > 0 && lines.length > 0 && total > 0;

  const commit = useCallback((input: NewPurchase) => recordPurchase(input), [recordPurchase]);
  const { run, busy, error, dismiss } = useMoneyAction(commit, () =>
    navigate('/purchase-done', { replace: true }),
  );

  useBackHandling(false, picking || addingSupplier ? 1 : 0, () => {
    setPicking(false);
    setAddingSupplier(false);
  });

  function addLine(product: Product) {
    const unit = receivableUnits(product)[0] ?? product.units[0]!.unit;
    setLines((current) => [
      ...current,
      {
        key: `${product.id}-${unit}-${current.length}`,
        productId: product.id,
        name: product.name,
        unit,
        qty: 1,
        unitCost: product.cost ?? 0,
      },
    ]);
    setPicking(false);
    setQuery('');
  }

  function patch(key: string, next: Partial<PurchaseLine>) {
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...next } : l)));
  }

  function save() {
    if (!valid) return;
    run({
      supplier,
      // The date chip sets the business day; the time of day is now, which is
      // accurate enough and keeps entries ordered within a day.
      at: new Date(`${day}T${new Date().toTimeString().slice(0, 8)}`).toISOString(),
      lines: lines.map(({ key: _key, ...line }) => line),
      paid,
      cash,
    });
  }

  const results = catalog.filter((p) =>
    !query.trim() ? true : p.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <>
      <AppBar title="ຮັບເຄື່ອງເຂົ້າຮ້ານ" onBack={goBack} />

      <div className={cx('vscroll', styles.body)}>
        <ErrorBanner message={error} onDismiss={dismiss} />

        <Labelled label="ຜູ້ຂາຍ">
          <Field value={supplier} onChange={setSupplier} label="ຜູ້ຂາຍ" placeholder="ເຊັ່ນ ບໍລິສັດ ຂ.ຂ." />
          {suppliers.length > 0 ? (
            <div className={cx('hscroll', styles.chips)}>
              {suppliers.map((s) => (
                <UnitChip key={s} unit={s} selected={s === supplier} onPress={() => setSupplier(s)} />
              ))}
              <button type="button" onClick={() => setAddingSupplier(true)} className={styles.addChip}>
                <IconPlus size={13} />
              </button>
            </div>
          ) : null}
        </Labelled>

        <Labelled label="ວັນທີຮັບເຄື່ອງ">
          <div className={styles.dateRow}>
            <input
              type="date"
              value={day}
              max={businessDay(new Date())}
              onChange={(e) => setDay(e.target.value || businessDay(new Date()))}
              aria-label="ວັນທີຮັບເຄື່ອງ"
              className={styles.dateInput}
            />
            {/* Echoed through fmtDate because the native picker renders in the
                device locale, and a lo-LA device shows Lao digits. */}
            <Mono size={11.5} color={colors.textMuted}>
              {fmtDate(new Date(`${day}T00:00:00`))}
            </Mono>
          </div>
        </Labelled>

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ລາຍການ
        </Text>

        {lines.map((line) => {
          const product = catalog.find((p) => p.id === line.productId);
          const units = product ? receivableUnits(product) : [line.unit];
          return (
            <Card key={line.key} className={styles.line}>
              <div className={styles.lineHead}>
                <Text weight={700} size={13.5} lines={1} className="grow">
                  {line.name}
                </Text>
                <button
                  type="button"
                  aria-label={`ເອົາ ${line.name} ອອກ`}
                  onClick={() => setLines((c) => c.filter((l) => l.key !== line.key))}
                  className={styles.remove}
                >
                  <IconClose size={15} />
                </button>
              </div>

              {units.length > 1 ? (
                <div className={styles.units} role="radiogroup" aria-label="ໜ່ວຍ">
                  {units.map((u) => (
                    <UnitChip
                      key={u}
                      unit={u}
                      selected={u === line.unit}
                      onPress={() => patch(line.key, { unit: u })}
                    />
                  ))}
                </div>
              ) : null}

              <div className={styles.lineFields}>
                <div className={styles.lineField}>
                  <Text size={10.5} color={colors.textMuted}>
                    ຈຳນວນ ({line.unit})
                  </Text>
                  <Field
                    value={String(line.qty)}
                    onChange={(v) => patch(line.key, { qty: parseQty(v) ?? 0 })}
                    kind="qty"
                    label={`ຈຳນວນ ${line.name}`}
                  />
                </div>
                <div className={styles.lineField}>
                  <Text size={10.5} color={colors.textMuted}>
                    ຕົ້ນທຶນ / {line.unit}
                  </Text>
                  <Field
                    value={line.unitCost ? String(line.unitCost) : ''}
                    onChange={(v) => patch(line.key, { unitCost: parseKip(v) ?? 0 })}
                    kind="kip"
                    label={`ຕົ້ນທຶນ ${line.name}`}
                  />
                </div>
              </div>

              <div className={styles.lineTotal}>
                <Text size={11} color={colors.textMuted} className="grow">
                  ລວມແຖວ
                </Text>
                <Mono weight={700} size={13}>
                  {fmt(Math.round(line.unitCost * line.qty))} ₭
                </Mono>
              </div>
            </Card>
          );
        })}

        <button type="button" onClick={() => setPicking(true)} className={styles.addLine}>
          <IconPlus size={15} />
          <Text weight={700} size={13} color={colors.navy}>
            ເພີ່ມແຖວ
          </Text>
        </button>

        <Labelled label="ຈ່າຍເງິນໃຫ້ຜູ້ຂາຍແລ້ວບໍ່?">
          <Segment
            value={paid}
            label="ສະຖານະການຈ່າຍ"
            options={[
              { value: true, label: 'ຈ່າຍແລ້ວ' },
              { value: false, label: 'ຄ້າງຈ່າຍ' },
            ]}
            onChange={setPaid}
          />
        </Labelled>

        {/* Only asked when there is a payment to describe. "ເງິນສົດ ຫຼື ໂອນ" is
            meaningless for a delivery taken on credit — nothing has been paid
            yet — and leaving it visible let the default silently pull money out
            of the drawer figure. */}
        {paid ? (
          <Labelled label="ຈ່າຍດ້ວຍຫຍັງ?">
            <Segment
              value={cash}
              label="ວິທີຈ່າຍ"
              options={[
                { value: true, label: 'ເງິນສົດ', sub: 'ຫັກຈາກລິ້ນຊັກ' },
                { value: false, label: 'ໂອນ', sub: 'ບໍ່ຫັກຈາກລິ້ນຊັກ' },
              ]}
              onChange={setCash}
            />
          </Labelled>
        ) : (
          <Text size={10.5} color={colors.textFaint}>
            ຄ້າງຈ່າຍ · ເງິນຍັງບໍ່ອອກຈາກລິ້ນຊັກ · ແອັບຍັງບໍ່ຕິດຕາມໜີ້ຜູ້ຂາຍ
          </Text>
        )}

        <Card className={styles.totalCard}>
          <Text weight={800} size={15} className="grow">
            ລວມ
          </Text>
          <Mono weight={700} size={20} className="selectable">
            {fmt(total)} ₭
          </Mono>
        </Card>

        <Button
          label={busy ? 'ກຳລັງບັນທຶກ...' : 'ບັນທຶກ ຮັບເຄື່ອງ'}
          disabled={!valid || busy}
          onPress={save}
        />
      </div>

      {picking ? (
        <div className={styles.picker} role="dialog" aria-label="ເລືອກສິນຄ້າ">
          <div className={styles.pickerSheet}>
            <div className={styles.searchRow}>
              <IconSearch size={16} className={styles.searchIcon} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ຄົ້ນຫາສິນຄ້າ..."
                aria-label="ຄົ້ນຫາສິນຄ້າ"
                autoFocus
                className={styles.searchInput}
              />
              <button type="button" aria-label="ປິດ" onClick={() => setPicking(false)}>
                <IconClose size={18} />
              </button>
            </div>
            <div className={styles.pickerList}>
              {results.map((p) => (
                <button key={p.id} type="button" onClick={() => addLine(p)} className={styles.pickRow}>
                  <div className="grow col">
                    <Text weight={700} size={13} lines={1} align="left">
                      {p.name}
                    </Text>
                    <Mono size={10.5} color={colors.textMuted} align="left">
                      {p.remaining === undefined ? 'ບໍ່ນັບສະຕັອກ' : `ຍັງເຫຼືອ ${p.remaining}`}
                      {p.cost ? ` · ຕົ້ນທຶນ ${fmt(p.cost)}` : ''}
                    </Mono>
                  </div>
                </button>
              ))}
              {results.length === 0 ? (
                <Text size={12} color={colors.textMuted} align="center" className={styles.empty}>
                  ບໍ່ພົບ · ເພີ່ມສິນຄ້າກ່ອນ
                </Text>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <AddValueSheet
        open={addingSupplier}
        title="ເພີ່ມຜູ້ຂາຍ"
        placeholder="ຊື່ຜູ້ຂາຍ"
        validate={(v) => (hasValue(suppliers, v) ? 'ມີຢູ່ແລ້ວ' : null)}
        onAdd={(value) => {
          addSupplier(value);
          setSupplier(value);
          setAddingSupplier(false);
        }}
        onClose={() => setAddingSupplier(false)}
      />
    </>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <Text weight={700} size={11} color={colors.textMuted}>
        {label}
      </Text>
      {children}
    </div>
  );
}
